import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  ProcurementTypeItemFragment,
  ProcurementTypesDataCollectionDocument,
  ToggleProcurementTypeEnablementDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import CreateRequestTypeModal from 'modules/procurement/components/procurementTypes/CreateTypeModal'
import DeleteRequestTypeModal from 'modules/procurement/components/procurementTypes/DeleteRequestTypeModal'
import DuplicateRequestTypeModal from 'modules/procurement/components/procurementTypes/DuplicateTypeModal'
import EditRequestTypeModal from 'modules/procurement/components/procurementTypes/EditRequestTypeModal'

import ProcurementTypesDataCollection from './procurementTypesDataCollection'

interface Props {
  hasFormsEnabled: boolean
}

const NewProcurementSettingsContent = ({ hasFormsEnabled }: Props) => {
  const [createProcurementTypeModalOpen, setCreateProcurementTypeModalOpen] = useState(false)
  const [editProcurementTypeModalOpen, setEditProcurementTypeModalOpen] = useState(false)
  const [deleteProcurementTypeModalOpen, setDeleteProcurementTypeModalOpen] = useState(false)
  const [duplicateProcurementTypeModalOpen, setDuplicateProcurementTypeModalOpen] = useState(false)
  const [selectedProcurementType, setSelectedProcurementType] =
    useState<ProcurementTypeItemFragment | null>(null)

  const navigate = useNavigate()

  const handleShowEditProcurementTypeFormModal = (procurementType: ProcurementTypeItemFragment) => {
    setSelectedProcurementType(procurementType)
    navigate(`/settings/procurement/${procurementType.id}`)
  }

  const handleShowEditProcurementTypeModal = (procurementType: ProcurementTypeItemFragment) => {
    setSelectedProcurementType(procurementType)
    setEditProcurementTypeModalOpen(true)
  }

  const handleShowDeleteProcurementTypeModal = (procurementType: ProcurementTypeItemFragment) => {
    setSelectedProcurementType(procurementType)
    setDeleteProcurementTypeModalOpen(true)
  }

  const handleShowDuplicateProcurementTypeModal = (
    procurementType: ProcurementTypeItemFragment
  ) => {
    setSelectedProcurementType(procurementType)
    setDuplicateProcurementTypeModalOpen(true)
  }

  const { company } = useCurrentSession()
  const [toggleProcurementTypeEnablement] = useMutation(ToggleProcurementTypeEnablementDocument, {
    extractorFn: (data) => data.procurementMutations.updateProcurementType,
  })

  const handleToggleProcurementType = async (procurementType: ProcurementTypeItemFragment) => {
    const result = await toggleProcurementTypeEnablement({
      variables: {
        id: procurementType.id,
        companyId: company.id,
        enabled: !procurementType.enabled,
      },
      refetchQueries: [ProcurementTypesDataCollectionDocument],
    })

    const handled = handleResult(result, () =>
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    )
    return handled
  }

  return (
    <>
      <Box width="full" paddingY="s24">
        <Box width="full" paddingY="s16">
          <Text size="400" weight="semibold">
            {i18n.t('procurement.purchase_requests.settings.types.title_new')}
          </Text>
          <Text size="300" weight="normal">
            {i18n.t('procurement.purchase_requests.settings.types.description_new')}
          </Text>
        </Box>
        <ProcurementTypesDataCollection
          onCreateProcurementType={() => setCreateProcurementTypeModalOpen(true)}
          // TODO: Replace with proper EditProcurementTypeForm logic
          onEditProcurementTypeForm={handleShowEditProcurementTypeFormModal}
          onEditProcurementType={handleShowEditProcurementTypeModal}
          onToggleProcurementType={handleToggleProcurementType}
          onDeleteProcurementType={handleShowDeleteProcurementTypeModal}
          onDuplicateProcurementType={handleShowDuplicateProcurementTypeModal}
        />
      </Box>
      {createProcurementTypeModalOpen && (
        <CreateRequestTypeModal
          onClose={() => setCreateProcurementTypeModalOpen(false)}
          hasFormsEnabled={hasFormsEnabled}
        />
      )}
      {duplicateProcurementTypeModalOpen && selectedProcurementType && (
        <DuplicateRequestTypeModal
          onClose={() => setDuplicateProcurementTypeModalOpen(false)}
          procurementType={selectedProcurementType}
        />
      )}
      {editProcurementTypeModalOpen && selectedProcurementType && (
        <EditRequestTypeModal
          requestType={selectedProcurementType}
          onClose={() => setEditProcurementTypeModalOpen(false)}
        />
      )}
      {deleteProcurementTypeModalOpen && (
        <DeleteRequestTypeModal
          onClose={() => setDeleteProcurementTypeModalOpen(false)}
          requestType={selectedProcurementType}
        />
      )}
    </>
  )
}

export default NewProcurementSettingsContent
